import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';

export default function CVGeneratorPage({ lang, theme, translations }) {
  const [isMobile, setIsMobile] = useState(false);
  const [inputText, setInputText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    setIsMobile(mediaQuery.matches);

    const handler = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handler);

    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const themeVars = theme === "dark" ? {
    bg: "#1a1a2e",
    fg: "#ffffff",
    card: "#16213e",
    btnBg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    btnFg: "#ffffff",
    accent: "#667eea",
    footerBg: "#0c0c1a",
    smallText: "#e0e0e0"
  } : {
    bg: "#ffffff",
    fg: "#333333",
    card: "#ffffff",
    btnBg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    btnFg: "#ffffff",
    accent: "#667eea",
    footerBg: "#f5f5f5",
    smallText: "#212121"
  };

  const handleTextChange = (e) => {
    setInputText(e.target.value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please upload a valid PDF file.');
    }
  };

  const generateCV = () => {
    if (!inputText && !selectedFile) {
      setError('Please provide either text input or a PDF file.');
      return;
    }
    setLoading(true);
    setError(null);
    // Placeholder for generation logic
    console.log('Generating CV...');
    setTimeout(() => setLoading(false), 2000); // Simulate
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: themeVars.bg,
      color: themeVars.fg,
      fontFamily: "Inter, Arial, sans-serif",
      display: "flex",
      flexDirection: "column",
    }}>
      {/* === ОСНОВНОЙ КОНТЕНТ === */}
      <main style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        padding: isMobile ? "0 12px" : "0 clamp(16px, 3vw, 40px)",
        boxSizing: "border-box",
      }}>
        {/* Header Section */}
        <div style={{
          textAlign: "center",
          marginBottom: isMobile ? "20px" : "60px",
          maxWidth: "min(100%, 1200px)",
          width: "100%",
          marginLeft: "auto",
          marginRight: "auto",
          padding: isMobile ? "20px 0" : "40px 0"
        }}>
          <h1 style={{
            fontSize: isMobile ? "2rem" : "3.5rem",
            fontWeight: "700",
            background: themeVars.btnBg,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: isMobile ? "10px" : "20px",
            lineHeight: "1.2"
          }}>
            {lang === 'fr' ? 'Générateur de CV' : 'CV Generator'}
          </h1>
          <p style={{
            fontSize: isMobile ? "1rem" : "1.5rem",
            color: themeVars.fg,
            opacity: 0.8,
            marginBottom: isMobile ? "16px" : "40px",
            lineHeight: "1.4"
          }}>
            {lang === 'fr' ? 'Créez un CV parfait avec l\'aide de l\'IA' : 'Create a perfect CV with AI assistance'}
          </p>

          <p style={{
            fontSize: isMobile ? "0.9rem" : "1.1rem",
            color: themeVars.fg,
            opacity: 0.7,
            lineHeight: "1.5",
            maxWidth: "700px",
            margin: "0 auto"
          }}>
            {lang === 'fr'
              ? 'Téléchargez un PDF ou entrez des informations pour générer un CV formaté professionnellement'
              : 'Upload a PDF or enter information to generate a professionally formatted CV'}
          </p>
        </div>

        {/* Input Section */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(260px, 1fr))",
          gap: isMobile ? "16px" : "30px",
          marginBottom: isMobile ? "40px" : "60px",
          maxWidth: "min(100%, 1200px)",
          width: "100%",
          marginLeft: "auto",
          marginRight: "auto",
          padding: isMobile ? "0 10px" : "0"
        }}>
          <div style={{
            background: themeVars.card,
            padding: isMobile ? "20px" : "28px",
            borderRadius: "15px",
            textAlign: "center",
            border: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            transition: "all 0.3s ease",
            width: "100%",
          }}>
            <h3 style={{
              fontSize: isMobile ? "1.1rem" : "1.3rem",
              marginBottom: "8px",
              color: themeVars.accent,
              fontWeight: "600"
            }}>
              {lang === 'fr' ? 'Entrer des informations CV' : 'Enter CV Information'}
            </h3>
            <textarea
              value={inputText}
              onChange={handleTextChange}
              rows={10}
              style={{
                width: '100%',
                marginBottom: '10px',
                background: themeVars.bg,
                color: themeVars.fg,
                border: `1px solid ${themeVars.accent}`,
                borderRadius: '8px',
                padding: '10px',
                fontSize: '0.95rem'
              }}
              placeholder={lang === 'fr' ? 'Entrez les détails de votre CV ici...' : 'Enter your resume details here...'}
            />
          </div>

          <div style={{
            background: themeVars.card,
            padding: isMobile ? "20px" : "28px",
            borderRadius: "15px",
            textAlign: "center",
            border: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            transition: "all 0.3s ease",
            width: "100%",
          }}>
            <h3 style={{
              fontSize: isMobile ? "1.1rem" : "1.3rem",
              marginBottom: "8px",
              color: themeVars.accent,
              fontWeight: "600"
            }}>
              {lang === 'fr' ? 'Télécharger PDF (optionnel)' : 'Upload PDF (optional)'}
            </h3>
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              style={{
                display: 'block',
                margin: '0 auto 10px',
                color: themeVars.fg
              }}
            />
            {selectedFile && <p style={{ color: themeVars.smallText }}>{selectedFile.name}</p>}
          </div>
        </div>

        {/* Generate Button */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: isMobile ? "40px" : "60px"
        }}>
          <button
            onClick={generateCV}
            disabled={loading}
            style={{
              background: themeVars.btnBg,
              color: themeVars.btnFg,
              border: "none",
              padding: isMobile ? "10px 20px" : "14px 28px",
              borderRadius: "22px",
              fontSize: isMobile ? "0.9rem" : "1.05rem",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
              transition: "all 0.2s ease"
            }}
          >
            {loading ? (lang === 'fr' ? 'Génération...' : 'Generating...') : (lang === 'fr' ? 'Générer CV' : 'Generate CV')}
          </button>
        </div>

        {error && <p style={{ color: 'red', textAlign: 'center', marginBottom: '20px' }}>{error}</p>}
      </main>

      {/* === ФУТЕР === */}
            {/* Footer */}
            <Footer lang={lang} theme={theme} />
    </div>
  );
}